import React, { useState } from "react";
import { Plus, Car, Edit2, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

var emptyVehicle = {
  brand: "",
  model: "",
  year: new Date().getFullYear(),
  cost: 0,
  price: 0,
  status: "available",
  clientId: null
};

function StockPage({ vehicles, onAddVehicle, onUpdateVehicle }) {
  var _useState = useState(false);
  var showModal = _useState[0];
  var setShowModal = _useState[1];

  var _useState2 = useState(null);
  var editing = _useState2[0];
  var setEditing = _useState2[1];

  var _useState3 = useState(emptyVehicle);
  var form = _useState3[0];
  var setForm = _useState3[1];

  function openNew() {
    setEditing(null);
    setForm(emptyVehicle);
    setShowModal(true);
  }

  function openEdit(v) {
    setEditing(v);
    setForm(v);
    setShowModal(true);
  }

  function handleSave() {
    if (!form.brand || !form.model) {
      toast.error("Marca y modelo son obligatorios");
      return;
    }
    if (editing) {
      onUpdateVehicle(editing.id, form);
      toast.success("Vehículo actualizado");
    } else {
      onAddVehicle(form);
      toast.success("Vehículo añadido");
    }
    setShowModal(false);
  }

  function fmt(n) {
    return new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n);
  }

  var statusLabels = {
    available: "Disponible",
    reserved: "Reservado",
    importing: "En importación",
    sold: "Vendido"
  };

  var statusColors = {
    available: "bg-green-100 text-green-700",
    reserved: "bg-orange-100 text-orange-700",
    importing: "bg-cyan-100 text-cyan-700",
    sold: "bg-slate-100 text-slate-700"
  };

  return (
    <div className="h-screen overflow-y-auto p-8 bg-slate-50" data-testid="stock-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-slate-800">Gestión de Stock</h1>
          <p className="text-slate-500">Control de vehículos disponibles y reservados</p>
        </div>
        <Button onClick={openNew} className="btn-primary gap-2">
          <Plus size={18} /> Añadir Vehículo
        </Button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">Disponibles</p>
          <p className="text-2xl font-bold text-green-600">{vehicles.filter(function(v) { return v.status === "available"; }).length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">Reservados</p>
          <p className="text-2xl font-bold text-orange-600">{vehicles.filter(function(v) { return v.status === "reserved"; }).length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">En importación</p>
          <p className="text-2xl font-bold text-cyan-600">{vehicles.filter(function(v) { return v.status === "importing"; }).length}</p>
        </div>
        <div className="bg-white rounded-lg p-4 border">
          <p className="text-sm text-slate-500">Margen Total Est.</p>
          <p className="text-2xl font-bold text-emerald-600">
            {fmt(vehicles.reduce(function(sum, v) { return sum + (v.price - v.cost); }, 0))}
          </p>
        </div>
      </div>

      {/* Vehicles Grid */}
      <div className="grid grid-cols-3 gap-6">
        {vehicles.map(function(v) {
          var margin = v.price - v.cost;
          var marginPct = v.cost > 0 ? ((margin / v.cost) * 100).toFixed(1) : 0;
          return (
            <div key={v.id} className="bg-white rounded-xl border shadow-sm overflow-hidden hover:shadow-md transition-shadow">
              <div className="p-5">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-slate-100 rounded-lg">
                      <Car size={24} className="text-slate-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-800">{v.brand} {v.model}</h3>
                      <p className="text-sm text-slate-500">{v.year}</p>
                    </div>
                  </div>
                  <button onClick={function() { openEdit(v); }} className="p-2 text-slate-400 hover:text-blue-600">
                    <Edit2 size={16} />
                  </button>
                </div>

                <div className="space-y-2 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Coste:</span>
                    <span className="font-medium">{fmt(v.cost)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Precio venta:</span>
                    <span className="font-medium">{fmt(v.price)}</span>
                  </div>
                  <div className="flex justify-between text-sm pt-2 border-t">
                    <span className="text-slate-500 flex items-center gap-1">
                      <TrendingUp size={14} /> Margen:
                    </span>
                    <span className="font-bold text-emerald-600">{fmt(margin)} ({marginPct}%)</span>
                  </div>
                </div>

                <span className={"inline-block text-xs font-medium px-3 py-1 rounded-full " + statusColors[v.status]}>
                  {statusLabels[v.status]}
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {/* Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? "Editar Vehículo" : "Nuevo Vehículo"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Marca *</Label>
                <Input value={form.brand} onChange={function(e) { setForm({ ...form, brand: e.target.value }); }} placeholder="BMW, Audi, Mercedes..." className="mt-1" />
              </div>
              <div>
                <Label>Modelo *</Label>
                <Input value={form.model} onChange={function(e) { setForm({ ...form, model: e.target.value }); }} placeholder="Serie 3, A4, GLC..." className="mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label>Año</Label>
                <Input type="number" value={form.year} onChange={function(e) { setForm({ ...form, year: parseInt(e.target.value) }); }} className="mt-1" />
              </div>
              <div>
                <Label>Coste (€)</Label>
                <Input type="number" value={form.cost} onChange={function(e) { setForm({ ...form, cost: parseFloat(e.target.value) || 0 }); }} className="mt-1" />
              </div>
              <div>
                <Label>Precio Venta (€)</Label>
                <Input type="number" value={form.price} onChange={function(e) { setForm({ ...form, price: parseFloat(e.target.value) || 0 }); }} className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Estado</Label>
              <Select value={form.status} onValueChange={function(v) { setForm({ ...form, status: v }); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="available">Disponible</SelectItem>
                  <SelectItem value="reserved">Reservado</SelectItem>
                  <SelectItem value="importing">En importación</SelectItem>
                  <SelectItem value="sold">Vendido</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setShowModal(false); }}>Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary">{editing ? "Guardar" : "Añadir"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default StockPage;
